import mirrorCreator from 'mirror-creator';

export const formNames = mirrorCreator(['SUBMISSION']);

export const questionTypes = mirrorCreator([
  'MultipleChoice',
  'MultipleResponse',
  'Programming',
  'TextResponse',
  'Comprehension',
  'FileUpload',
  'Scribing',
  'VoiceResponse',
  'ForumPostResponse',
  'RubricBasedResponse',
]);

export const MEGABYTES_TO_BYTES = 1024 * 1024;

export const BUFFER_TIME_TO_FORCE_SUBMIT_MS = 5 * 1000;

// calculate how long has it passed since the student starts the submission
// to still be considered a "newly created" submission
export const TIME_LAPSE_NEW_SUBMISSION_MS = 10 * 1000;

export const EVALUATE_POLL_INTERVAL_MILLISECONDS = 500;
export const FEEDBACK_POLL_INTERVAL_MILLISECONDS = 2000;

export const workflowStates = {
  Unstarted: 'unstarted' as const,
  Attempting: 'attempting' as const,
  Submitted: 'submitted' as const,
  Graded: 'graded' as const,
  Published: 'published' as const,
};

export const TestCaseTypes = {
  Public: 'public_test',
  Private: 'private_test',
  Evaluation: 'evaluation_test',
};

export const SCRIBING_POPOVER_TYPES = [
  'TYPE',
  'DRAW',
  'LINE',
  'SHAPE',
  'LAYER',
] as const;
export type ScribingPopoverType = (typeof SCRIBING_POPOVER_TYPES)[number];

export const SCRIBING_TOOLS_WITH_COLOR = [
  'TYPE',
  'DRAW',
  'LINE',
  'SHAPE_BORDER',
  'SHAPE_FILL',
] as const;
export type ScribingToolWithColor = (typeof SCRIBING_TOOLS_WITH_COLOR)[number];

export const SCRIBING_TOOLS_WITH_THICKNESS = [
  'DRAW',
  'LINE',
  'SHAPE_BORDER',
] as const;
export type ScribingToolWithThickness =
  (typeof SCRIBING_TOOLS_WITH_THICKNESS)[number];

export const SCRIBING_TOOLS_WITH_LINE_STYLE = ['LINE', 'SHAPE_BORDER'] as const;
export type ScribingToolWithLineStyle =
  (typeof SCRIBING_TOOLS_WITH_LINE_STYLE)[number];

const SCRIBING_TOOLS = [
  'TYPE',
  'DRAW',
  'LINE',
  'SHAPE',
  'SELECT',
  'MOVE',
  'UNDO',
  'REDO',
  'ZOOM_IN',
  'ZOOM_OUT',
  'DELETE',
] as const;
export type ScribingTool = (typeof SCRIBING_TOOLS)[number];

const SCRIBING_SHAPES = ['RECT', 'ELLIPSE'] as const;
export type ScribingShape = (typeof SCRIBING_SHAPES)[number];

export const defaultPastAnswersDisplayed = 3;

const actionTypes = mirrorCreator([
  'PURGE_SUBMISSION_STORE',
  'FETCH_SUBMISSION_REQUEST',
  'FETCH_SUBMISSION_SUCCESS',
  'FETCH_SUBMISSION_FAILURE',
  'SUBMISSION_BLOCKED',
  'AUTOGRADE_SUBMISSION_REQUEST',
  'AUTOGRADE_SUBMISSION_SUCCESS',
  'AUTOGRADE_SUBMISSION_FAILURE',
  'SAVE_ANSWER_REQUEST',
  'SAVE_ANSWER_SUCCESS',
  'SAVE_ANSWER_FAILURE',
  'FINALISE_REQUEST',
  'FINALISE_SUCCESS',
  'FINALISE_FAILURE',
  'UNSUBMIT_REQUEST',
  'UNSUBMIT_SUCCESS',
  'UNSUBMIT_FAILURE',
  'AUTOGRADE_REQUEST',
  'AUTOGRADE_SUBMITTED',
  'AUTOGRADE_SUCCESS',
  'AUTOGRADE_RUBRIC_SUCCESS',
  'AUTOGRADE_FAILURE',
  'AUTOGRADE_SAVING_SUCCESS',
  'AUTOGRADE_SAVING_FAILURE',
  'FEEDBACK_REQUEST',
  'FEEDBACK_SUCCESS',
  'FEEDBACK_FAILURE',
  'REEVALUATE_REQUEST',
  'REEVALUATE_SUBMITTED',
  'REEVALUATE_SUCCESS',
  'REEVALUATE_FAILURE',
  'RESET_REQUEST',
  'RESET_SUCCESS',
  'RESET_FAILURE',
  'SAVE_ALL_GRADE_REQUEST',
  'SAVE_ALL_GRADE_SUCCESS',
  'SAVE_ALL_GRADE_FAILURE',
  'SAVE_GRADE_REQUEST',
  'SAVE_GRADE_SUCCESS',
  'SAVE_GRADE_FAILURE',
  'MARK_REQUEST',
  'MARK_SUCCESS',
  'MARK_FAILURE',
  'UNMARK_REQUEST',
  'UNMARK_SUCCESS',
  'UNMARK_FAILURE',
  'PUBLISH_REQUEST',
  'PUBLISH_SUCCESS',
  'PUBLISH_FAILURE',
  'CREATE_COMMENT_REQUEST',
  'CREATE_COMMENT_SUCCESS',
  'CREATE_COMMENT_FAILURE',
  'CREATE_COMMENT_CHANGE',
  'UPDATE_COMMENT_REQUEST',
  'UPDATE_COMMENT_SUCCESS',
  'UPDATE_COMMENT_FAILURE',
  'UPDATE_COMMENT_CHANGE',
  'DELETE_COMMENT_REQUEST',
  'DELETE_COMMENT_SUCCESS',
  'DELETE_COMMENT_FAILURE',
  'CREATE_ANNOTATION_REQUEST',
  'CREATE_ANNOTATION_SUCCESS',
  'CREATE_ANNOTATION_FAILURE',
  'CREATE_ANNOTATION_CHANGE',
  'UPDATE_ANNOTATION_REQUEST',
  'UPDATE_ANNOTATION_SUCCESS',
  'UPDATE_ANNOTATION_FAILURE',
  'UPDATE_ANNOTATION_CHANGE',
  'DELETE_ANNOTATION_REQUEST',
  'DELETE_ANNOTATION_SUCCESS',
  'DELETE_ANNOTATION_FAILURE',
  'DELETE_ATTACHMENT_REQUEST',
  'DELETE_ATTACHMENT_SUCCESS',
  'DELETE_ATTACHMENT_FAILURE',
  'UPDATE_GRADING',
  'UPDATE_RUBRIC',
  'UPDATE_EXP',
  'UPDATE_MULTIPLIER',
  'ENTER_STUDENT_VIEW',
  'EXIT_STUDENT_VIEW',

  'FETCH_SUBMISSIONS_REQUEST',
  'FETCH_SUBMISSIONS_SUCCESS',
  'FETCH_SUBMISSIONS_FAILURE',
  'FETCH_SUBMISSIONS_FROM_KODITSU_REQUEST',
  'FETCH_SUBMISSIONS_FROM_KODITSU_SUCCESS',
  'FETCH_SUBMISSIONS_FROM_KODITSU_FAILURE',
  'PUBLISH_SUBMISSIONS_REQUEST',
  'PUBLISH_SUBMISSIONS_SUCCESS',
  'PUBLISH_SUBMISSIONS_FAILURE',
  'FORCE_SUBMIT_SUBMISSIONS_REQUEST',
  'FORCE_SUBMIT_SUBMISSIONS_SUCCESS',
  'FORCE_SUBMIT_SUBMISSIONS_FAILURE',
  'SEND_ASSESSMENT_REMINDER_REQUEST',
  'SEND_ASSESSMENT_REMINDER_SUCCESS',
  'SEND_ASSESSMENT_REMINDER_FAILURE',
  'DOWNLOAD_SUBMISSIONS_FILES_REQUEST',
  'DOWNLOAD_SUBMISSIONS_FILES_SUCCESS',
  'DOWNLOAD_SUBMISSIONS_FILES_FAILURE',
  'DOWNLOAD_SUBMISSIONS_CSV_REQUEST',
  'DOWNLOAD_SUBMISSIONS_CSV_SUCCESS',
  'DOWNLOAD_SUBMISSIONS_CSV_FAILURE',
  'DOWNLOAD_STATISTICS_REQUEST',
  'DOWNLOAD_STATISTICS_SUCCESS',
  'DOWNLOAD_STATISTICS_FAILURE',
  'UNSUBMIT_SUBMISSION_REQUEST',
  'UNSUBMIT_SUBMISSION_SUCCESS',
  'UNSUBMIT_SUBMISSION_FAILURE',
  'UNSUBMIT_ALL_SUBMISSIONS_REQUEST',
  'UNSUBMIT_ALL_SUBMISSIONS_SUCCESS',
  'UNSUBMIT_ALL_SUBMISSIONS_FAILURE',
  'DELETE_SUBMISSION_REQUEST',
  'DELETE_SUBMISSION_SUCCESS',
  'DELETE_SUBMISSION_FAILURE',
  'DELETE_ALL_SUBMISSIONS_REQUEST',
  'DELETE_ALL_SUBMISSIONS_SUCCESS',
  'DELETE_ALL_SUBMISSIONS_FAILURE',

  // Answer action types
  'UPDATE_ANSWER_CLIENT_VERSION',

  // Scribing answer action types
  'SET_CANVAS_LOADED',
  'FETCH_SCRIBING_QUESTION_REQUEST',
  'FETCH_SCRIBING_QUESTION_SUCCESS',
  'FETCH_SCRIBING_QUESTION_FAILURE',
  'FETCH_SCRIBING_ANSWER_REQUEST',
  'FETCH_SCRIBING_ANSWER_SUCCESS',
  'FETCH_SCRIBING_ANSWER_FAILURE',
  'UPDATE_SCRIBING_ANSWER_REQUEST',
  'UPDATE_SCRIBING_ANSWER_SUCCESS',
  'UPDATE_SCRIBING_ANSWER_FAILURE',
  'UPDATE_SCRIBING_ANSWER_IN_LOCAL',

  // Text Response answer action types
  'UPLOAD_TEXT_RESPONSE_FILES_REQUEST',
  'UPLOAD_TEXT_RESPONSE_FILES_SUCCESS',
  'UPLOAD_TEXT_RESPONSE_FILES_FAILURE',

  // Programming answer action types
  'DELETE_PROGRAMMING_FILE_REQUEST',
  'DELETE_PROGRAMMING_FILE_SUCCESS',
  'DELETE_PROGRAMMING_FILE_FAILURE',
  'UPLOAD_PROGRAMMING_FILES_REQUEST',
  'UPLOAD_PROGRAMMING_FILES_SUCCESS',
  'UPLOAD_PROGRAMMING_FILES_FAILURE',

  // Fetch annotations for single answer
  'FETCH_ANNOTATION_SUCCESS',
]);

export default actionTypes;
